import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Save, Image, Plus, Trash2, CheckCircle, XCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import MediaPicker from '../components/MediaPicker';
import { apiFetch } from '../lib/api';
import {
  BrandStoryPage,
  BrandStorySection,
  DEFAULT_BRAND_STORY_SECTIONS,
} from './BrandStory';
import { DEFAULT_HOME_SECTIONS } from '../lib/homeDefaults';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

interface PickerTarget {
  sectionIndex: number;
  update: (url: string) => void;
}

interface SlugConfig {
  label: string;
  defaultSections: BrandStorySection[];
  previewPath: string;
  saveButton: string;
  menuId: string;
}

const SLUG_CONFIG: Record<string, SlugConfig> = {
  home: {
    label: '首頁內容',
    defaultSections: DEFAULT_HOME_SECTIONS as BrandStorySection[],
    previewPath: '/',
    saveButton: '儲存首頁內容',
    menuId: 'home-page',
  },
  'brand-story': {
    label: '品牌故事',
    defaultSections: DEFAULT_BRAND_STORY_SECTIONS,
    previewPath: '/about',
    saveButton: '儲存品牌故事',
    menuId: 'brand-story',
  },
};

function classNames(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

function Field({
  label,
  children,
  required,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
}) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function ImageField({
  url,
  alt,
  onChoose,
}: {
  url?: string;
  alt: string;
  onChoose: () => void;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-24 h-24 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 overflow-hidden flex-shrink-0">
        {url ? (
          <img
            src={url}
            alt={alt}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
            <Image size={20} />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onChoose}
        className="px-3 py-2 text-sm font-medium text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg hover:bg-brand-100 dark:hover:bg-brand-900/30 transition-colors"
      >
        選擇圖片
      </button>
    </div>
  );
}

const AdminPageEditor: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const config = slug ? SLUG_CONFIG[slug] : undefined;
  const effectiveSlug = config ? slug! : 'brand-story';
  const effectiveConfig = config || SLUG_CONFIG['brand-story'];

  const [page, setPage] = useState<BrandStoryPage>({
    title: effectiveConfig.label,
    description: '',
    sections: effectiveConfig.defaultSections,
    meta: {},
    isActive: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { can } = usePermissions();
  const canWriteCms = can(PERMISSIONS.CMS_WRITE);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pickerTarget, setPickerTarget] = useState<PickerTarget | null>(null);

  useEffect(() => {
    fetchPage();
  }, [effectiveSlug]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/public/admin/cms/pages/${effectiveSlug}`);
      if (res.ok) {
        const data = await res.json();
        setPage({
          title: data.title || effectiveConfig.label,
          description: data.description || '',
          sections: Array.isArray(data.sections) && data.sections.length > 0
            ? data.sections
            : effectiveConfig.defaultSections,
          meta: data.meta || {},
          isActive: data.isActive ?? true,
        });
      } else if (res.status === 404) {
        setPage({
          title: effectiveConfig.label,
          description: '',
          sections: effectiveConfig.defaultSections,
          meta: {},
          isActive: true,
        });
      } else {
        setPage({
          title: effectiveConfig.label,
          description: '',
          sections: effectiveConfig.defaultSections,
          meta: {},
          isActive: true,
        });
        setMessage({ type: 'error', text: '無法載入現有內容，已載入預設資料' });
      }
    } catch {
      setPage({
        title: effectiveConfig.label,
        description: '',
        sections: effectiveConfig.defaultSections,
        meta: {},
        isActive: true,
      });
      setMessage({ type: 'error', text: '無法載入現有內容，已載入預設資料' });
    } finally {
      setLoading(false);
    }
  };

  const updateSection = (index: number, updater: (section: BrandStorySection) => BrandStorySection) => {
    setPage((prev) => ({
      ...prev,
      sections: prev.sections.map((s, i) => (i === index ? updater(s) : s)),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await apiFetch(`/api/public/admin/cms/pages/${effectiveSlug}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: page.title,
          description: page.description,
          sections: page.sections,
          meta: page.meta,
          isActive: page.isActive,
        }),
      });
      if (res.ok) {
        setMessage({ type: 'success', text: `${effectiveConfig.label}已儲存` });
      } else {
        const data = await res.json().catch(() => ({}));
        setMessage({ type: 'error', text: data.error || '儲存失敗' });
      }
    } catch {
      setMessage({ type: 'error', text: '儲存失敗，請檢查網絡連接' });
    } finally {
      setSaving(false);
    }
  };

  const renderSectionEditor = (section: BrandStorySection, index: number) => {
    switch (section.type) {
      case 'hero': {
        const s = section;
        return (
          <div className="space-y-4">
            <Field label="標題">
              <input
                type="text"
                value={s.title}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), title: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
              />
            </Field>
            <Field label="副標題">
              <input
                type="text"
                value={s.subtitle}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), subtitle: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="描述">
              <textarea
                value={s.description}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), description: e.target.value }))
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
              />
            </Field>
            <Field label="背景圖片">
              <ImageField
                url={s.backgroundImage}
                alt="背景圖片"
                onChoose={() =>
                  setPickerTarget({
                    sectionIndex: index,
                    update: (url) =>
                      updateSection(index, (sec) => ({ ...(sec as typeof s), backgroundImage: url })),
                  })
                }
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="主要按鈕文字">
                <input
                  type="text"
                  value={s.ctaPrimary.label}
                  onChange={(e) =>
                    updateSection(index, (sec) => ({
                      ...(sec as typeof s),
                      ctaPrimary: { ...s.ctaPrimary, label: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </Field>
              <Field label="主要按鈕連結">
                <input
                  type="text"
                  value={s.ctaPrimary.url}
                  onChange={(e) =>
                    updateSection(index, (sec) => ({
                      ...(sec as typeof s),
                      ctaPrimary: { ...s.ctaPrimary, url: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </Field>
              <Field label="次要按鈕文字">
                <input
                  type="text"
                  value={s.ctaSecondary.label}
                  onChange={(e) =>
                    updateSection(index, (sec) => ({
                      ...(sec as typeof s),
                      ctaSecondary: { ...s.ctaSecondary, label: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </Field>
              <Field label="次要按鈕連結">
                <input
                  type="text"
                  value={s.ctaSecondary.url}
                  onChange={(e) =>
                    updateSection(index, (sec) => ({
                      ...(sec as typeof s),
                      ctaSecondary: { ...s.ctaSecondary, url: e.target.value },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </Field>
            </div>
          </div>
        );
      }

      case 'mission': {
        const s = section;
        return (
          <div className="space-y-4">
            <Field label="標題">
              <input
                type="text"
                value={s.title}
                onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="描述">
              <textarea
                value={s.description}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), description: e.target.value }))
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
              />
            </Field>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">核心價值</label>
              <div className="space-y-3">
                {s.values.map((value, vIdx) => (
                  <div
                    key={vIdx}
                    className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <input
                        type="text"
                        placeholder="價值標題"
                        value={value.title}
                        onChange={(e) => {
                          const newValues = [...s.values];
                          newValues[vIdx] = { ...value, title: e.target.value };
                          updateSection(index, (sec) => ({ ...(sec as typeof s), values: newValues }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newValues = s.values.filter((_, i) => i !== vIdx);
                          updateSection(index, (sec) => ({ ...(sec as typeof s), values: newValues }));
                        }}
                        className="md:justify-self-end inline-flex items-center gap-1 px-3 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <Trash2 size={14} />
                        刪除
                      </button>
                    </div>
                    <textarea
                      placeholder="價值描述"
                      value={value.description}
                      onChange={(e) => {
                        const newValues = [...s.values];
                        newValues[vIdx] = { ...value, description: e.target.value };
                        updateSection(index, (sec) => ({ ...(sec as typeof s), values: newValues }));
                      }}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    updateSection(index, (sec) => ({
                      ...(sec as typeof s),
                      values: [...s.values, { title: '', description: '' }],
                    }))
                  }
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 rounded-lg hover:bg-brand-100 dark:hover:bg-brand-900/30 transition-colors"
                >
                  <Plus size={14} />
                  新增價值
                </button>
              </div>
            </div>
          </div>
        );
      }

      case 'about': {
        const s = section;
        return (
          <div className="space-y-4">
            <Field label="標題">
              <input
                type="text"
                value={s.title}
                onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="描述">
              <textarea
                value={s.description}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), description: e.target.value }))
                }
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
              />
            </Field>
            <Field label="圖片">
              <ImageField
                url={s.imageUrl}
                alt={s.imageAlt}
                onChoose={() =>
                  setPickerTarget({
                    sectionIndex: index,
                    update: (url) => updateSection(index, (sec) => ({ ...(sec as typeof s), imageUrl: url })),
                  })
                }
              />
            </Field>
            <Field label="圖片替代文字">
              <input
                type="text"
                value={s.imageAlt}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), imageAlt: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
          </div>
        );
      }

      case 'vocationalTraining': {
        const s = section;
        return (
          <div className="space-y-4">
            <Field label="標題">
              <input
                type="text"
                value={s.title}
                onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="描述">
              <textarea
                value={s.description}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), description: e.target.value }))
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
              />
            </Field>
            <Field label="標誌">
              <ImageField
                url={s.logoUrl}
                alt="標誌"
                onChoose={() =>
                  setPickerTarget({
                    sectionIndex: index,
                    update: (url) => updateSection(index, (sec) => ({ ...(sec as typeof s), logoUrl: url })),
                  })
                }
              />
            </Field>
            <Field label="特色列表（每行一項）">
              <textarea
                value={s.features.join('\n')}
                onChange={(e) =>
                  updateSection(index, (sec) => ({
                    ...(sec as typeof s),
                    features: e.target.value.split('\n').filter((line) => line.trim() !== ''),
                  }))
                }
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
              />
            </Field>
            <Field label="圖片">
              <ImageField
                url={s.imageUrl}
                alt={s.title}
                onChoose={() =>
                  setPickerTarget({
                    sectionIndex: index,
                    update: (url) => updateSection(index, (sec) => ({ ...(sec as typeof s), imageUrl: url })),
                  })
                }
              />
            </Field>
          </div>
        );
      }

      case 'corporateSupport': {
        const s = section;
        return (
          <div className="space-y-4">
            <Field label="標題">
              <input
                type="text"
                value={s.title}
                onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="描述">
              <textarea
                value={s.description}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), description: e.target.value }))
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
              />
            </Field>
            <Field label="圖片">
              <ImageField
                url={s.imageUrl}
                alt={s.title}
                onChoose={() =>
                  setPickerTarget({
                    sectionIndex: index,
                    update: (url) => updateSection(index, (sec) => ({ ...(sec as typeof s), imageUrl: url })),
                  })
                }
              />
            </Field>
          </div>
        );
      }

      case 'leverage': {
        const s = section;
        return (
          <div className="space-y-4">
            <Field label="Badge">
              <input
                type="text"
                value={s.badge}
                onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), badge: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="標題">
              <input
                type="text"
                value={s.title}
                onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="描述">
              <textarea
                value={s.description}
                onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="左邊圖片">
                <ImageField
                  url={s.leftImageUrl}
                  alt={s.leftTitle}
                  onChoose={() =>
                    setPickerTarget({
                      sectionIndex: index,
                      update: (url) => updateSection(index, (sec) => ({ ...(sec as typeof s), leftImageUrl: url })),
                    })
                  }
                />
              </Field>
              <Field label="右邊圖片">
                <ImageField
                  url={s.rightImageUrl}
                  alt={s.rightTitle}
                  onChoose={() =>
                    setPickerTarget({
                      sectionIndex: index,
                      update: (url) => updateSection(index, (sec) => ({ ...(sec as typeof s), rightImageUrl: url })),
                    })
                  }
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="左邊標題">
                <input
                  type="text"
                  value={s.leftTitle}
                  onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), leftTitle: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </Field>
              <Field label="右邊標題">
                <input
                  type="text"
                  value={s.rightTitle}
                  onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), rightTitle: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="左邊描述">
                <textarea
                  value={s.leftDescription}
                  onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), leftDescription: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
                />
              </Field>
              <Field label="右邊描述">
                <textarea
                  value={s.rightDescription}
                  onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), rightDescription: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="對比前文字">
                <input
                  type="text"
                  value={s.beforeLabel}
                  onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), beforeLabel: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </Field>
              <Field label="對比後文字">
                <input
                  type="text"
                  value={s.afterLabel}
                  onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), afterLabel: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </Field>
            </div>
          </div>
        );
      }

      case 'mediaCoverage': {
        const s = section;
        return (
          <div className="space-y-4">
            <Field label="標題">
              <input
                type="text"
                value={s.title}
                onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="描述">
              <textarea
                value={s.description}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), description: e.target.value }))
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
              />
            </Field>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">報導項目</label>
              <div className="space-y-4">
                {s.items.map((item, iIdx) => (
                  <div
                    key={iIdx}
                    className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <div className="flex flex-col sm:flex-row gap-4 mb-3">
                      <div className="w-full sm:w-32 h-24 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 overflow-hidden flex-shrink-0">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.caption}
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                            <Image size={20} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 space-y-3">
                        <input
                          type="text"
                          placeholder="說明"
                          value={item.caption}
                          onChange={(e) => {
                            const newItems = [...s.items];
                            newItems[iIdx] = { ...item, caption: e.target.value };
                            updateSection(index, (sec) => ({ ...(sec as typeof s), items: newItems }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setPickerTarget({
                              sectionIndex: index,
                              update: (url) => {
                                const newItems = [...s.items];
                                newItems[iIdx] = { ...item, imageUrl: url };
                                updateSection(index, (sec) => ({ ...(sec as typeof s), items: newItems }));
                              },
                            })
                          }
                          className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 rounded-lg hover:bg-brand-100 dark:hover:bg-brand-900/30 transition-colors"
                        >
                          <Image size={14} />
                          選擇圖片
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = s.items.filter((_, i) => i !== iIdx);
                        updateSection(index, (sec) => ({ ...(sec as typeof s), items: newItems }));
                      }}
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <Trash2 size={14} />
                      刪除項目
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    updateSection(index, (sec) => ({
                      ...(sec as typeof s),
                      items: [...s.items, { imageUrl: '', caption: '' }],
                    }))
                  }
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 rounded-lg hover:bg-brand-100 dark:hover:bg-brand-900/30 transition-colors"
                >
                  <Plus size={14} />
                  新增報導項目
                </button>
              </div>
            </div>
          </div>
        );
      }

      case 'successStories': {
        const s = section;
        return (
          <div className="space-y-4">
            <Field label="標題">
              <input
                type="text"
                value={s.title}
                onChange={(e) => updateSection(index, (sec) => ({ ...(sec as typeof s), title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">分享語錄</label>
              <div className="space-y-3">
                {s.quotes.map((q, qIdx) => (
                  <div
                    key={qIdx}
                    className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                  >
                    <textarea
                      placeholder="語錄內容"
                      value={q.quote}
                      onChange={(e) => {
                        const newQuotes = [...s.quotes];
                        newQuotes[qIdx] = { ...q, quote: e.target.value };
                        updateSection(index, (sec) => ({ ...(sec as typeof s), quotes: newQuotes }));
                      }}
                      rows={3}
                      className="w-full px-3 py-2 mb-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
                    />
                    <div className="flex gap-3">
                      <input
                        type="text"
                        placeholder="作者"
                        value={q.author}
                        onChange={(e) => {
                          const newQuotes = [...s.quotes];
                          newQuotes[qIdx] = { ...q, author: e.target.value };
                          updateSection(index, (sec) => ({ ...(sec as typeof s), quotes: newQuotes }));
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newQuotes = s.quotes.filter((_, i) => i !== qIdx);
                          updateSection(index, (sec) => ({ ...(sec as typeof s), quotes: newQuotes }));
                        }}
                        className="inline-flex items-center gap-1 px-3 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <Trash2 size={14} />
                        刪除
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    updateSection(index, (sec) => ({
                      ...(sec as typeof s),
                      quotes: [...s.quotes, { quote: '', author: '' }],
                    }))
                  }
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 rounded-lg hover:bg-brand-100 dark:hover:bg-brand-900/30 transition-colors"
                >
                  <Plus size={14} />
                  新增語錄
                </button>
              </div>
            </div>
          </div>
        );
      }

      case 'footer': {
        const s = section;
        return (
          <div className="space-y-4">
            <Field label="標誌">
              <ImageField
                url={s.logoUrl}
                alt="頁尾標誌"
                onChoose={() =>
                  setPickerTarget({
                    sectionIndex: index,
                    update: (url) => updateSection(index, (sec) => ({ ...(sec as typeof s), logoUrl: url })),
                  })
                }
              />
            </Field>
            <Field label="地址">
              <input
                type="text"
                value={s.address}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), address: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="電話">
                <input
                  type="text"
                  value={s.phone}
                  onChange={(e) =>
                    updateSection(index, (sec) => ({ ...(sec as typeof s), phone: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </Field>
              <Field label="WhatsApp 號碼（只填數字）">
                <input
                  type="text"
                  value={s.whatsapp}
                  onChange={(e) =>
                    updateSection(index, (sec) => ({ ...(sec as typeof s), whatsapp: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                />
              </Field>
            </div>
            <Field label="電郵">
              <input
                type="email"
                value={s.email}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), email: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="Facebook 連結">
              <input
                type="text"
                value={s.facebookUrl}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), facebookUrl: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="Facebook 圖標">
              <ImageField
                url={s.facebookIcon}
                alt="Facebook 圖標"
                onChoose={() =>
                  setPickerTarget({
                    sectionIndex: index,
                    update: (url) =>
                      updateSection(index, (sec) => ({ ...(sec as typeof s), facebookIcon: url })),
                  })
                }
              />
            </Field>
            <Field label="版權文字">
              <input
                type="text"
                value={s.copyright}
                onChange={(e) =>
                  updateSection(index, (sec) => ({ ...(sec as typeof s), copyright: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
          </div>
        );
      }

      default:
        return null;
    }
  };

  const sectionTitles: Record<string, string> = {
    hero: 'Hero 主視覺',
    mission: '使命 Mission',
    about: '關於我們 About',
    vocationalTraining: '職業訓練',
    corporateSupport: '企業協作',
    leverage: '善的槓桿',
    mediaCoverage: '媒體報導',
    successStories: '成功故事',
    footer: '頁尾 Footer',
  };

  if (loading) {
    return (
      <AdminLayout currentPage={effectiveConfig.menuId}>
        <div className="flex flex-col items-center justify-center py-20 text-gray-500 dark:text-gray-400">
          <Loader2 size={32} className="animate-spin mb-3" />
          <p>載入中...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage={effectiveConfig.menuId}>
      <div className="max-w-5xl mx-auto">
        {message && (
          <div
            className={classNames(
              'mb-6 p-4 rounded-lg flex items-center justify-between',
              message.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-900/30'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/30'
            )}
          >
            <span className="flex items-center gap-2">
              {message.type === 'success' ? <CheckCircle size={18} /> : <XCircle size={18} />}
              {message.text}
            </span>
            <button onClick={() => setMessage(null)} className="hover:opacity-70">✕</button>
          </div>
        )}

        {/* Top settings */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4">頁面設定</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="頁面標題" required>
              <input
                type="text"
                value={page.title}
                onChange={(e) => setPage((p) => ({ ...p, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </Field>
            <Field label="Slug">
              <input
                type="text"
                value={effectiveSlug}
                disabled
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              />
            </Field>
          </div>
          <Field label="頁面描述（SEO）">
            <textarea
              value={page.description}
              onChange={(e) => setPage((p) => ({ ...p, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
            />
          </Field>
          <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={page.isActive}
              onChange={(e) => setPage((p) => ({ ...p, isActive: e.target.checked }))}
              className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">頁面啟用</span>
          </label>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {page.sections.map((section, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between">
                <h3 className="font-bold text-gray-800 dark:text-white">
                  {sectionTitles[section.type] || section.type}
                </h3>
                <span className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                  {section.type}
                </span>
              </div>
              <div className="p-6">{renderSectionEditor(section, index)}</div>
            </div>
          ))}
        </div>

        {/* Save */}
        <div className="mt-8 flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => window.open(effectiveConfig.previewPath, '_blank')}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            預覽頁面
          </button>
          {canWriteCms && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2.5 text-white font-bold bg-brand-600 hover:bg-brand-700 rounded-lg shadow-sm disabled:opacity-60 transition-colors"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              {saving ? '儲存中...' : effectiveConfig.saveButton}
            </button>
          )}
        </div>
      </div>

      <MediaPicker
        isOpen={!!pickerTarget}
        onClose={() => setPickerTarget(null)}
        onSelect={(url) => {
          pickerTarget?.update(url);
          setPickerTarget(null);
        }}
        folder="story"
      />
    </AdminLayout>
  );
};

export default AdminPageEditor;
